import { useEffect, useRef, useState } from 'react';

import { getSiteCmsImageDataApi } from '@/lib/api/siteCmsApi';
import type { SiteCmsOwnerType } from '@shared/types/entities';
import { useQuery } from '@tanstack/react-query';

/** Defer authenticated image bytes until the thumbnail approaches the viewport. */
export default function SiteCmsPrivateImage({
  ownerType,
  ownerId,
  id,
  alt,
}: {
  ownerType: SiteCmsOwnerType;
  ownerId: number;
  id: string;
  alt: string;
}) {
  const target = useRef<HTMLSpanElement>(null);
  const [visible, setVisible] = useState(() => typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    if (visible || !target.current) return;
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(e => e.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '180px' },
    );
    observer.observe(target.current);
    return () => observer.disconnect();
  }, [visible]);
  const query = useQuery({
    queryKey: ['site-cms', 'image-data', ownerType, ownerId, id],
    queryFn: () => getSiteCmsImageDataApi(ownerType, ownerId, id),
    enabled: visible,
    staleTime: Infinity,
    retry: false,
  });
  return (
    <span ref={target} className='cms-private-image'>
      {query.data ? (
        <img src={query.data} alt={alt} loading='lazy' />
      ) : (
        <span className='cms-media-placeholder'>{query.isError ? 'Image indisponible' : 'Chargement…'}</span>
      )}
    </span>
  );
}
